// Move a catalog item to another kind. Admin-only, beside Rename on the item
// page. Behavior and why: web/docs/features/item-kinds.md, "Changing an item's kind".
import React, { useMemo, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import Dialog from '../components/ui/Dialog';
import { Alert } from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import { laneInput as inputClass, laneLabel as labelClass } from '../components/review/fieldStyles';
import KindPicker from './KindPicker';
import SkuBuilder, { emptyBoard, skuIsWellFormed } from './SkuBuilder';
import { parseBoardSku, previewBoardSku, type BoardFields } from './skuGrammar';
import useCodedVendors from './useCodedVendors';
import type { ItemKind } from '../stores/useItemKindsStore';

export interface ChangeKindResult {
  row: { id: number; sku: string; kind_key: string | null };
  previous_sku: string;
  batch_assignments_moved: number;
  from_kind_key: string | null;
  to_kind_key: string;
}

interface Props {
  itemId: number;
  currentSku: string;
  currentKindKey: string | null;
  /** The item's MPN, so "New kind…" can tell a category from a part number. */
  mpn?: string | null;
  onClose: () => void;
  onChanged: (result: ChangeKindResult) => void;
}

const noop = () => {};

const ChangeKindModal: React.FC<Props> = ({ itemId, currentSku, currentKindKey, mpn, onClose, onChanged }) => {
  const api = useApi();
  const toast = useToast();
  const codedVendors = useCodedVendors(api);

  const [kind, setKind] = useState<ItemKind | null>(null);
  const [board, setBoard] = useState<BoardFields>(emptyBoard());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const isBoard = kind?.code_mode === 'structured';
  const boardSku = isBoard ? previewBoardSku(board) : '';

  const problem = useMemo(() => {
    if (!kind) return null;
    if (kind.key === currentKindKey) return 'That is already this item\'s kind.';
    if (isBoard && !skuIsWellFormed('board', boardSku)) return 'Finish the board code. Rev major and manufacturer code are both needed.';
    if (isBoard && boardSku === currentSku) return 'That is already this item\'s code.';
    return null;
  }, [kind, currentKindKey, isBoard, boardSku, currentSku]);

  const canSubmit = !!kind && !problem && !saving;

  const chooseKind = (chosen: ItemKind) => {
    setKind(chosen);
    if (chosen.code_mode === 'structured') {
      // A board that was already a board keeps its segments: a re-kind is a
      // correction, and the revision and assembler are still true.
      const parsed = parseBoardSku(currentSku);
      setBoard({ ...(parsed || emptyBoard()), board: chosen.key });
    }
  };

  const submit = async () => {
    if (!canSubmit || !kind) return;
    setSaving(true);
    try {
      const result = (await api.post(`/parts-catalog/${itemId}/change-kind`, {
        body: {
          kind_key: kind.key,
          ...(isBoard ? { sku: boardSku } : {}),
          reason: reason.trim() || null,
        },
      })) as ChangeKindResult;
      toast.success(`Now ${result.row.sku}, a ${kind.label || kind.key}. ${currentSku} still scans.`);
      onChanged(result);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That kind change could not be completed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      title="Change kind"
      onClose={onClose}
      size="xl"
      busy={saving}
      dirty={kind !== null || reason.trim() !== ''}
      onSubmit={submit}
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClass('quiet')}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} className={buttonClass('filled')}>
            {saving ? 'Changing…' : 'Change kind'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-surface-container-low px-3 py-2.5">
          <p className="text-xs text-on-surface-variant leading-relaxed">
            <span className="font-mono text-primary">{currentSku}</span> keeps working. The item gets a
            new code under the new kind, and the old one is kept as a former name that still finds it.
            Nothing on a shelf needs relabeling.
          </p>
        </div>

        <KindPicker
          id="change-kind-picker"
          label="New kind"
          autoFocus
          value={kind?.key || null}
          onChange={chooseKind}
          partNumberHint={mpn || null}
          inputClassName={inputClass}
          labelClassName={labelClass}
        />

        <Reveal show={!!kind && !isBoard && kind.key !== currentKindKey}>
          <p className="text-xs text-on-surface-variant">
            Gets a fresh <span className="font-mono">{kind?.key}-XXXX</span> code, assigned when you save.
          </p>
        </Reveal>

        <Reveal show={isBoard}>
          <SkuBuilder
            shape="board"
            onShapeChange={noop}
            board={board}
            onBoardChange={setBoard}
            nonBoard={{ brand: board.brand, typeToken: '', generation: board.generation, tail: '1' }}
            onNonBoardChange={noop}
            codedVendors={codedVendors}
            labelClass={labelClass}
            lockBoard
          />
        </Reveal>

        <Alert show={!!problem && !!kind} tone="warning" variant="text" live="alert">{problem}</Alert>

        <label className="block">
          <span className={labelClass}>
            Why <span className="normal-case tracking-normal text-on-surface-variant">(optional)</span>
          </span>
          <Field
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. minted as a board, it is a purchased module"
          />
          <p className="text-xs text-on-surface-variant mt-1.5 leading-snug">
            Kept with the old name, for the next person who wonders why the code changed.
          </p>
        </label>
      </div>
    </Dialog>
  );
};

export default ChangeKindModal;
